import { useEffect, useState } from 'react';
import _ from 'lodash';
import { UserActions } from 'BMapsCmds';
import { useSelector } from 'BMapsRedux';
import { setSidePanelWidth } from 'BMapsRedux/projectState/access';
import { useColorSchemeInfo } from 'BMapsRedux/prefs/access';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { SidePanelContainer } from 'BMapsUI/info_display/SidePanel';
import TabController from 'BMapsUI/common/TabController';
import tabs from './Tabs';

export function ScreeningSidePanel({
    open, handleClose, tabId='new_screen_tab', initData={},
}) {
    const tableId = 'screeningTable';
    const { sidePanelWidth, activeChart } = useSelector(
        (state) => state.projectState.tableState[tableId]
    );

    const { textCss } = useColorSchemeInfo();

    const [width, setWidth] = useState(sidePanelWidth);
    const [highlightedJobId, setHighlightedJobId] = useState(null);
    const [resizing, setResizing] = useState(false);

    function onMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();
        setResizing(true);
    }

    function setTabId(tab) {
        openScreeningSidePanel(tab, initData);
    }

    useEffect(() => {
        // This 0 debounce prevents a "too many renders" error that can happen when resizing quickly
        const debouncedSetWidth = _.debounce((newWidth) => setWidth(newWidth), 0);
        function onMouseUp() {
            setResizing(false);
            setSidePanelWidth(width, tableId);
            EventBroker.publish('resize');
            debouncedSetWidth.cancel();
        }
        function onMouseMove(e) {
            e.preventDefault();
            e.stopPropagation();
            const newWidth = e.clientX - 10;
            if (newWidth > 20) {
                debouncedSetWidth(newWidth);
            }
        }
        if (resizing) {
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('mousemove', onMouseMove);
            return () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                debouncedSetWidth.cancel();
            };
        }
        return undefined;
    }, [resizing]);

    return (
        <div style={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
            <SidePanelContainer
                open={open}
                handleClose={handleClose}
                title="Screening"
                width={width}
                contentStyle={{
                    padding: '0.5rem',
                }}
            >
                <TabController
                    tabs={tabs.map((tab) => tab.forTabController(
                        {
                            initData,
                            highlightedJobId,
                            setHighlightedJobId,
                            activeChart,
                        }
                    ))}
                    selected={tabId}
                    setSelected={setTabId}
                    style={{
                        flex: 1,
                    }}
                />
            </SidePanelContainer>
            <div
                role="toolbar"
                onMouseDown={onMouseDown}
                style={{
                    cursor: 'ew-resize',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginRight: '.5rem',
                }}
            >
                <i className="fa fa-arrows-h" style={{ color: textCss }} />
            </div>
        </div>
    );
}

export function openScreeningSidePanel(tabId, initData) {
    const validTabId = tabs.find((t) => t.id === tabId)?.id || tabs[0].id;
    UserActions.OpenSidePanel('left', {
        pageId: 'screening',
        props: {
            initData,
            tabId: validTabId,
        },
        component: <ScreeningSidePanel />,
        longLived: false,
    });
}

// Accepts either the object form `{tabId, initData}` — which is how the Gibbs pre-fill handoff
// arrives (UICmds.openScreeningPanel) — or a bare tab-id string, so the Jobs Management links
// that only want to switch tabs are unaffected. Identical to the Free Energy panel's
// subscription, whose comment already claimed this one had the same shape; it did not. This
// handler dropped `initData` on the floor, which is why the chemist's chosen library never
// reached a panel that was already written to receive it.
EventBroker.subscribe('askOpenScreeningPanel', (event, payload) => {
    if (payload && typeof payload === 'object') {
        openScreeningSidePanel(payload.tabId, payload.initData);
    } else {
        openScreeningSidePanel(payload);
    }
});
